/**
 * OKS-555 — §17 Cheers: reaction contract + shared-prop shapes.
 *
 * A peer cheer (stored as a `kind:'cheer'` kudos row, OKS-509) carries one of
 * four reactions + an optional ≤140 message. Mirrors the backend `Kudo::REACTIONS`
 * closed set verbatim — never rename FE-side without a matching model change.
 */

/** The 4 reactions a cheer can express (order = compose-row order). */
export type CheerReaction = "fire" | "clap" | "raise" | "hundred"

/** Ordered reaction keys for the compose quick-reacts row. */
export const CHEER_REACTIONS: readonly CheerReaction[] = [
  "fire",
  "clap",
  "raise",
  "hundred",
]

/** Emoji + i18n label key per reaction (label lives in `cheers.reactions.*`). */
export const CHEER_REACTION_META: Record<
  CheerReaction,
  { emoji: string; labelKey: string }
> = {
  fire: { emoji: "🔥", labelKey: "cheers.reactions.fire" },
  clap: { emoji: "👏", labelKey: "cheers.reactions.clap" },
  raise: { emoji: "🙌", labelKey: "cheers.reactions.raise" },
  hundred: { emoji: "💯", labelKey: "cheers.reactions.hundred" },
}

/** Max cheer message length (matches `Kudo::MAX_CHEER_MESSAGE_LENGTH`). */
export const CHEER_MESSAGE_MAX = 140

/**
 * `pending_cheer` shared-prop shape (OKS-555) — the single most-recent un-seen,
 * un-reported cheer the viewer received, driving the live "your moment" modal.
 * `null` when there's nothing to celebrate. Emitted by
 * `ApplicationController#pending_cheer_props` — display identity only, no brand
 * name / raw timestamps (isolation floor 90).
 */
export interface PendingCheer {
  /** Kudo id — keys the seen-dismiss + report POSTs. */
  kudo_id: number
  reaction: CheerReaction
  /** Optional message the cheerer added (≤140); null when reaction-only. */
  message: string | null
  /** Cheerer display name; "" → handle/initials fallback. */
  sender_name: string
  /** Cheerer TikTok handle (no leading `@`); null → name only. */
  sender_handle: string | null
  /** Cheerer avatar URL; null → initials fallback. */
  avatar_url: string | null
  /**
   * The cheerer's most-recent feed post the viewer has NOT already cheered
   * (the Cheer-back target), or null when no such post exists — the Cheer-back
   * action soft-disables and `cheer_back_blocked_reason` says why.
   *
   * OKS-943 — this is NOT simply "their most recent post". Posts the viewer
   * already holds a cheer on are excluded, because sending there would UPDATE
   * that cheer in place rather than create a new one (BRIEF §1).
   */
  cheer_back_submission_id: number | null
  /**
   * OKS-943 — why Cheer-back is unavailable, or null when it IS available.
   *
   * Drives which helper string renders under the disabled button. The
   * distinction is load-bearing: after the retarget, reusing the "they have no
   * post" copy for the all-cheered case would state something the creator can
   * see is false (BRIEF §3 R6).
   *
   * ⚠️ Invariant: EXACTLY ONE of `cheer_back_submission_id` /
   * `cheer_back_blocked_reason` is non-null. Never both, never neither.
   */
  cheer_back_blocked_reason: CheerBackBlockedReason | null
}

/**
 * OKS-943 — why the Cheer-back action is unavailable.
 *
 *   - `no_posts`     — the cheerer has no approved crew-feed post at all.
 *   - `all_cheered`  — they have posts, but the viewer has cheered every one.
 *
 * A closed enum by design: it carries no brand identity, no name and no count,
 * so it is safe on the shared-prop pipe (isolation floor 90). It is only
 * brand-safe because the exclusion behind it is scoped to the current brand
 * (BRIEF §3 R5) — an unscoped exclusion would let a cheer in another brand
 * change which string this creator is shown.
 */
export type CheerBackBlockedReason = "no_posts" | "all_cheered"
